import React, { useEffect, useRef, useState } from "react";
import api from "../services/api";
import { usePoll } from "../lib/hooks";
import { fmtTime, num } from "../lib/format";
import { Badge, Button, Callout, Card, CopyButton, Segmented, Skeleton, cx } from "../components/ui";
import { Icon } from "../components/Icon";
import { PageHeader } from "../components/ConsoleShell";

// Kept at module level so the conversation survives switching pages (cleared on reload).
const store = { msgs: [] };
let seq = 0;
const uid = () => `m${Date.now()}-${seq++}`;

const WINDOWS = [
  { id: 1, label: "1 hour" },
  { id: 24, label: "24 hours" },
  { id: 168, label: "7 days" },
];
const windowLabel = (h) => WINDOWS.find((w) => w.id === h)?.label || `${h} hours`;

const PROMPTS = (h) => [
  `Summarise the last ${windowLabel(h)} for a shift handover`,
  "Which sources should we prioritise, and why?",
  "Map our top attack types to MITRE ATT&CK techniques",
  "Write a Sigma rule for the most common attack type",
  "Draft a five-line briefing for leadership",
  "What remediation steps fit our top threats?",
];

// ── Minimal, safe markdown (bold, italics, code, headings, lists, fences) ──
const INLINE = /(\*\*[^*\n]+\*\*|`[^`\n]+`|\b_[^_\n]+_\b|\*[^*\s][^*\n]*\*)/g;
function inline(text) {
  return text.split(INLINE).map((p, i) => {
    if (i % 2 === 0) return p;
    if (p.startsWith("**")) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith("`")) return <code key={i}>{p.slice(1, -1)}</code>;
    return <em key={i}>{p.slice(1, -1)}</em>;
  });
}

function Markdown({ text }) {
  const out = [];
  let para = [];
  let list = null;
  let code = null;
  const flushPara = () => {
    if (para.length) out.push(<p key={out.length}>{inline(para.join("\n"))}</p>);
    para = [];
  };
  const flushList = () => {
    if (!list) return;
    const Tag = list.ordered ? "ol" : "ul";
    out.push(
      <Tag key={out.length}>
        {list.items.map((it, i) => (
          <li key={i}>{inline(it)}</li>
        ))}
      </Tag>,
    );
    list = null;
  };
  for (const raw of String(text || "").replace(/\r/g, "").split("\n")) {
    if (code) {
      if (/^\s*```/.test(raw)) {
        out.push(<pre key={out.length} className="code-block">{code.join("\n")}</pre>);
        code = null;
      } else code.push(raw);
      continue;
    }
    if (/^\s*```/.test(raw)) {
      flushPara();
      flushList();
      code = [];
      continue;
    }
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) {
      flushPara();
      flushList();
      continue;
    }
    if (/^\s*(-{3,}|\*{3,})$/.test(line)) {
      flushPara();
      flushList();
      out.push(<hr key={out.length} />);
      continue;
    }
    const h = /^#{1,4}\s+(.*)$/.exec(line);
    if (h) {
      flushPara();
      flushList();
      out.push(<h4 key={out.length}>{inline(h[1])}</h4>);
      continue;
    }
    const b = /^\s*[-*•]\s+(.*)$/.exec(line);
    const n = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (b || n) {
      flushPara();
      if (!list || list.ordered !== !!n) {
        flushList();
        list = { ordered: !!n, items: [] };
      }
      list.items.push((b || n)[1]);
      continue;
    }
    if (list && /^\s{2,}\S/.test(raw)) {
      list.items[list.items.length - 1] += ` ${line.trim()}`;
      continue;
    }
    flushList();
    para.push(line);
  }
  if (code) out.push(<pre key={out.length} className="code-block">{code.join("\n")}</pre>);
  flushPara();
  flushList();
  return out;
}

function Message({ m, onRetry }) {
  const user = m.role === "user";
  const fallback = m.model === "fallback";
  return (
    <div className={cx("msg", user && "msg-user")}>
      <span className="msg-avatar" aria-hidden="true">
        <Icon name={user ? "user" : "sparkle"} size={15} />
      </span>
      <div style={{ minWidth: 0 }}>
        <span className="sr-only">{user ? "You asked:" : "Analyst replied:"}</span>
        <div className={cx("msg-bubble", !user && "msg-md", m.error && "is-error")}>
          {user ? m.content : m.error ? <p>{m.content}</p> : <Markdown text={m.content} />}
        </div>
        <div className={cx("msg-meta", "row")} style={{ gap: 8, justifyContent: user ? "flex-end" : "flex-start" }}>
          <span>{fmtTime(m.at)}</span>
          {!user && !m.error && (
            <>
              <span aria-hidden="true">·</span>
              <span>{fallback ? "Rule-based reply (Claude offline)" : m.model}</span>
              {!!m.tokens && (
                <>
                  <span aria-hidden="true">·</span>
                  <span>{num(m.tokens)} tokens</span>
                </>
              )}
              <CopyButton value={m.content} label="Copy reply" />
            </>
          )}
          {m.error && (
            <Button size="sm" variant="ghost" icon="refresh" onClick={onRetry}>
              Try again
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusCard({ status }) {
  const s = status.data;
  const online = s?.available && s?.api_key_set;
  return (
    <Card title="Status" icon="radio">
      {!s && !status.error ? (
        <Skeleton h={44} />
      ) : status.error ? (
        <Callout tone="critical" icon="alertCircle" title="Status unavailable">
          The backend did not answer. Replies may fail until it is back.
        </Callout>
      ) : online ? (
        <div className="col" style={{ gap: 8 }}>
          <Badge tone="ok" dot size="lg">
            Claude connected
          </Badge>
          <span className="small muted">
            Model <span className="mono">{s.model}</span>. Each question is answered with a fresh summary of live data.
          </span>
        </div>
      ) : (
        <div className="col" style={{ gap: 8 }}>
          <Badge tone="medium" dot size="lg">
            Offline: rule-based replies
          </Badge>
          <span className="small muted">
            {!s.available ? "The Anthropic SDK is not installed on the server." : "ANTHROPIC_API_KEY is not set on the server."} Answers come from a small set of
            canned responses until it is configured.
          </span>
        </div>
      )}
    </Card>
  );
}

export default function Assistant() {
  const status = usePoll("/api/ai/status", { interval: 60000 });
  const [hours, setHours] = useState(24);
  const [msgs, setMsgsState] = useState(store.msgs);
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState(false);
  const logRef = useRef(null);
  const inputRef = useRef(null);

  const setMsgs = (fn) =>
    setMsgsState((m) => {
      const next = typeof fn === "function" ? fn(m) : fn;
      store.msgs = next;
      return next;
    });

  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs, pending]);

  const resize = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };
  useEffect(resize, [draft]);

  const send = async (text, base = msgs) => {
    const q = String(text ?? draft).trim();
    if (!q || pending) return;
    const history = base.filter((m) => !m.error).map(({ role, content }) => ({ role, content }));
    setMsgs([...base, { id: uid(), role: "user", content: q, at: Date.now() }]);
    setDraft("");
    setPending(true);
    try {
      const r = await api.post("/api/ai/chat", { message: q, history, hours }, { timeout: 90000 });
      const d = r.data || {};
      setMsgs((m) => [
        ...m,
        { id: uid(), role: "assistant", content: d.reply || "(no reply)", model: d.model, tokens: (d.input_tokens || 0) + (d.output_tokens || 0), at: Date.now() },
      ]);
    } catch (e) {
      const detail = e?.response?.data?.detail || (e.code === "ECONNABORTED" ? "The analyst took too long to answer." : e.message);
      setMsgs((m) => [...m, { id: uid(), role: "assistant", error: true, content: `Couldn't get a reply: ${detail}`, retry: q, at: Date.now() }]);
    } finally {
      setPending(false);
      inputRef.current?.focus();
    }
  };

  const retry = (i) => send(msgs[i].retry, msgs.slice(0, Math.max(0, i - 1)));

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Intelligence"
        icon="sparkle"
        title="AI analyst"
        description="Investigate your environment with answers grounded in detection data."
        actions={<Segmented label="Data window" value={hours} onChange={setHours} options={WINDOWS} />}
      />
      <div className="grid cols-12">
        <Card
          className="span-8"
          flush
          title="Conversation"
          icon="message"
          actions={
            <Button size="sm" variant="ghost" icon="plus" disabled={!msgs.length || pending} onClick={() => setMsgs([])}>
              New conversation
            </Button>
          }
        >
          <div className="chat">
            <div className="chat-log" ref={logRef} role="log" aria-live="polite" aria-label="Conversation with the AI analyst">
              {!msgs.length && (
                <div className="chat-intro">
                  <span className="chat-intro-icon">
                    <Icon name="sparkle" size={22} />
                  </span>
                  <h3>What do you want to know?</h3>
                  <p className="muted">Pick a starting point or type your own question. The analyst sees the last {windowLabel(hours)} of activity.</p>
                  <div className="prompt-chips">
                    {PROMPTS(hours).map((p) => (
                      <button key={p} type="button" className="chip" onClick={() => send(p)}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {msgs.map((m, i) => (
                <Message key={m.id} m={m} onRetry={() => retry(i)} />
              ))}
              {pending && (
                <div className="msg">
                  <span className="msg-avatar" aria-hidden="true">
                    <Icon name="sparkle" size={15} />
                  </span>
                  <div className="msg-bubble">
                    <span className="typing" role="status" aria-label="The analyst is writing">
                      <i />
                      <i />
                      <i />
                    </span>
                  </div>
                </div>
              )}
            </div>
            <form
              className="chat-compose"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <textarea
                ref={inputRef}
                className="textarea"
                rows={1}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKey}
                placeholder="Ask about threats, sources, techniques or next steps…"
                aria-label="Message the AI analyst"
                maxLength={2000}
              />
              <Button type="submit" variant="primary" icon="send" loading={pending} disabled={!draft.trim()} aria-label="Send message">
                Send
              </Button>
            </form>
          </div>
        </Card>
        <div className="span-4 stack">
          <StatusCard status={status} />
          <Card title="What the analyst sees" icon="eye">
            <ul className="check-list">
              <li>Event and threat totals for the last {windowLabel(hours)}</li>
              <li>Critical and high detection counts</li>
              <li>Top eight attack types and top five source IPs</li>
              <li>Threat-intelligence feed summary</li>
              <li>The last six messages of this conversation</li>
            </ul>
            <p className="small muted" style={{ marginTop: 12 }}>
              Individual events and raw logs are not included unless you paste them into a question. When Claude is connected, the question and this summary are
              sent to Anthropic's API.
            </p>
          </Card>
          <Callout tone="info" icon="info" title="Verify before you act">
            Treat answers as a starting point. Confirm in Detections or Investigations before blocking a source or closing a case.
          </Callout>
        </div>
      </div>
    </>
  );
}
